import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { CheckCircle2, ChevronRight, FileCheck2, RefreshCw } from 'lucide-react';
import { apiErrorMessage, getFacilityVersionWorkQueue } from '../api/abfApi';
import { amendmentReviewPath, onboardingVersionPath } from '../routes';
import { businessState } from '../presentation/contractPresentation';
import type { FacilityContractVersion } from '../types/contractWorkspace';
import { PrerequisiteAction } from '../components/PrerequisiteAction';

const Shell = styled.section`
  margin: 0 0 1rem;
  padding: 0.8rem;
  border: 1px solid rgba(56, 189, 248, 0.22);
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(14, 116, 144, 0.12), rgba(15, 23, 42, 0.55));
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Title = styled.div`
  color: #f8fafc;
  font-size: 0.95rem;
  font-weight: 850;
`;

const Copy = styled.div`
  margin-top: 0.3rem;
  color: #94a3b8;
  font-size: 0.72rem;
  line-height: 1.45;
`;

const Queue = styled.div`
  display: grid;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const Item = styled.button`
  display: grid;
  grid-template-columns: minmax(15rem, 1.4fr) minmax(8rem, 0.7fr) minmax(11rem, 1fr) auto;
  gap: 0.8rem;
  align-items: center;
  padding: 0.7rem;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 10px;
  background: rgba(2, 6, 23, 0.34);
  color: #cbd5e1;
  cursor: pointer;
  text-align: left;
  @media (max-width: 850px) { grid-template-columns: 1fr; }
`;

const Label = styled.div`
  color: #64748b;
  font-size: 0.57rem;
  font-weight: 850;
  letter-spacing: 0.07em;
  text-transform: uppercase;
`;

const Value = styled.div`
  margin-top: 0.25rem;
  color: #f8fafc;
  font-size: 0.75rem;
  font-weight: 800;
`;

const Status = styled.span<{ $ready: boolean }>`
  display: inline-flex;
  margin-top: 0.25rem;
  padding: 0.2rem 0.45rem;
  border: 1px solid ${p => p.$ready ? 'rgba(52, 211, 153, 0.38)' : 'rgba(251, 191, 36, 0.34)'};
  border-radius: 999px;
  color: ${p => p.$ready ? '#6ee7b7' : '#fcd34d'};
  font-size: 0.58rem;
  font-weight: 850;
`;

const Empty = styled.div<{ $error?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin-top: 0.75rem;
  color: ${p => p.$error ? '#fca5a5' : '#6ee7b7'};
  font-size: 0.72rem;
`;

const date = (value: string | null) => value
  ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(`${value.slice(0, 10)}T00:00:00`))
  : 'Not specified';

/** Whose move it is, in the reader's words. */
const nextActionLabel = (
  verdict: FacilityContractVersion['readiness'], state: string,
): string => {
  if (!verdict || verdict.unavailable) return businessState(state);
  switch (String(verdict.nextAction || '')) {
    case 'ACTIVATE': return 'Ready for you to activate';
    case 'WAIT_FOR_CHECKER': return 'Waiting for a second person';
    case 'RESOLVE': return verdict.actorMayPrepare ? 'Needs your review' : 'Needs review';
    case 'DONE': return 'Activated';
    default: return state === 'SCHEDULED' ? 'Approved, takes effect on its date' : businessState(state);
  }
};

const FacilityOnboardingQueue: React.FC = () => {
  const navigate = useNavigate();
  const [versions, setVersions] = useState<FacilityContractVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setVersions(await getFacilityVersionWorkQueue());
    } catch (reason) {
      setError(apiErrorMessage(reason, 'Unable to load the agreement review queue.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The queue opens the contract review, always. Routing a version that already
  // had a facility to the workspace contract tab sent every UI-lane version — and
  // every document version after its facility was minted — to the superseded
  // surface instead of the review standard (F-UIL-012). The review page covers
  // the immutable phases too: an active version reads as the recorded outcome.
  // Slice 6: an amendment (a version with a predecessor) opens its own review,
  // grouped by consequence; an onboarding version keeps the contract review.
  const open_ = (version: FacilityContractVersion) => navigate(
    version.predecessor_version_id != null && version.facility_id != null
      ? amendmentReviewPath(version.facility_id, version.id)
      : onboardingVersionPath(version.id));

  return (
    <Shell>
      <Header>
        <div>
          <Title>Agreement and amendment work queue</Title>
          <Copy>Document review is carried forward. This queue contains only unresolved contract evidence and independent activation decisions.</Copy>
        </div>
        <PrerequisiteAction disabled={loading} disabledReason={loading ? 'The agreement queue is already refreshing.' : undefined} onClick={load}>
          <RefreshCw size={14} /> Refresh
        </PrerequisiteAction>
      </Header>
      {error && <Empty $error>{error}</Empty>}
      {!error && !loading && versions.length === 0 && <Empty><CheckCircle2 size={15} /> No agreements or amendments await review or activation.</Empty>}
      {versions.length > 0 && (
        <Queue>
          {versions.map(version => {
            // The verdict the server computed for this reader, not the stored
            // status, which can be stale under today's rules (F-WR-021).
            const verdict = version.readiness;
            const open = Number(verdict?.openItems ?? 0);
            const blockers = verdict?.blockers?.length ?? 0;
            const ready = Boolean(verdict?.canActivate);
            const summary = version.terms.filter(term => term.required_for_activation);
            const verified = summary.filter(term => ['VERIFIED', 'ACTIVE'].includes(term.state)).length;
            const detail = verdict?.unavailable
              ? 'Readiness could not be checked'
              : open > 0
                ? `${open} item${open === 1 ? '' : 's'} to resolve`
                : blockers > 0
                  ? String(verdict?.blockers?.[0]?.message || 'Blocked')
                  : `${verified} of ${summary.length} required terms approved`;
            return (
              <Item key={version.id} onClick={() => open_(version)}>
                <div><Label>Facility</Label><Value>{version.facility_name || version.facility_code || 'New facility agreement'}</Value></div>
                <div><Label>Proposed authority</Label><Value>{version.predecessor_version_id != null ? 'Amendment · version' : 'Version'} {version.version_no} · {date(version.effective_from)}</Value>{['SCHEDULED', 'WITHDRAWN'].includes(version.state) && <Copy>{businessState(version.state)}</Copy>}</div>
                <div><Label>Readiness</Label><Status $ready={ready}>{nextActionLabel(verdict, version.state)}</Status><Copy>{detail}</Copy></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#7dd3fc', fontSize: 11, fontWeight: 850 }}>
                  <FileCheck2 size={15} /> Review <ChevronRight size={14} />
                </div>
              </Item>
            );
          })}
        </Queue>
      )}
    </Shell>
  );
};

export default FacilityOnboardingQueue;
